// Type definitions for Keycloak configuration
interface KeycloakConfig {
  baseUrl: string;
  realm: string;
  clientId: string;
  redirectUri?: string;
}

// Standard OAuth2/OIDC parameters
interface StandardOAuthParams {
  response_type?: 'code' | 'token' | 'id_token';
  scope?: string;
  redirect_uri?: string;
  state?: string;
  nonce?: string;
  client_id: string;
}

// Keycloak-specific parameters
interface KeycloakParams {
  login_hint?: string;
  kc_idp_hint?: string;
  ui_locales?: string;
  prompt?: 'none' | 'login' | 'consent' | 'select_account';
  max_age?: number;
  acr_values?: string;
}

interface CustomParams {
  [key: string]: string | number | boolean | string[] | undefined;
}

// Combined parameter types
export type KeycloakLoginParams = Partial<StandardOAuthParams & KeycloakParams & CustomParams>;

/**
 * Builds and navigates to a Keycloak login URL with typed parameters
 * @param config - Keycloak configuration
 * @param additionalParams - Additional query parameters (optional)
 * @param newTab - Whether to open in a new tab (optional)
 */
export function navigateToKeycloakLogin(
  config: KeycloakConfig,
  additionalParams: KeycloakLoginParams = {},
): void {
  // Remove trailing slashes from base URL
  const cleanBaseUrl = config.baseUrl.replace(/\/+$/, '');

  // Construct the Keycloak authorization endpoint URL
  const authUrl = `${cleanBaseUrl}/realms/${encodeURIComponent(config.realm)}/protocol/openid-connect/auth`;

  // Define standard OAuth2/OIDC parameters
  const standardParams: StandardOAuthParams = {
    client_id: config.clientId,
    response_type: 'code',
    scope: 'openid',
    redirect_uri: config.redirectUri,
  };

  // Merge standard and additional parameters
  const params = {
    ...standardParams,
    ...additionalParams,
  };

  // Create URL object
  const url = new URL(authUrl);
  const searchParams = new URLSearchParams();

  // Add all parameters, handling arrays and null/undefined
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return; // Skip null/undefined values
    }

    if (Array.isArray(value)) {
      // Handle array values
      value.forEach((item) => searchParams.append(key, item.toString()));
    } else {
      searchParams.append(key, value.toString());
    }
  });

  // Set the search parameters on the URL
  url.search = searchParams.toString();

  // Navigate to the URL
  window.location.href = url.toString();
}
